import React, { useState, useRef, useCallback } from 'react';
import { Popper as MuiPopper, ClickAwayListener } from '@mui/material';
import { useAuth } from '@contexts/AuthContext';
import { useScreen } from '@hooks/useScreen';
import { openProfile } from '@helpers/profile';
import { UserAvatar } from '@components/Community/index.js';
import { IconRosetteDiscountCheckFilled, IconSelector } from "@tabler/icons-react";
import { Text } from '@fragments';
import UserDropdown from './UserDropdown';

import './styles.scss';

// `variant="card"` (default): the sidebar card, opening upward. `variant="avatar"`:
// only the avatar, for a header — the same hover menu opens below it, and the
// language sub-menu opens to its left (there is no room on the right).
// `items` ({ id, label, icon, onClick }) are rows the host adds above the profile
// settings (the Studio's "Gerenciar assinaturas"); picking one closes the menu.
export default function UserSelect({ variant = 'card', items = [] }) {
    const avatarOnly = variant === 'avatar';
    const { user, handleOpenProfileConfigModal } = useAuth();
    const { appearance, setAppearance, language, setLanguage, languages } = useScreen();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const closeTimerRef = useRef(null);

    // While the language picker (a sub-popper portaled to <body>) is open, the
    // hover-out and click-away that normally dismiss this menu are suspended:
    // the pointer is legitimately outside the panel while it sits on the picker.
    const languageMenuOpenRef = useRef(false);
    const pointerInsideRef = useRef(false);

    const cancelClose = useCallback(() => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    // Hover menu: a short grace delay bridges the gap between the trigger and the
    // dropdown so moving the cursor across it doesn't dismiss the menu.
    const scheduleClose = useCallback(() => {
        if (languageMenuOpenRef.current) return;
        cancelClose();
        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            setOpen(false);
        }, 160);
    }, [cancelClose]);

    const handleToggle = () => setOpen(prev => !prev);
    const handleOpen = () => { cancelClose(); setOpen(true); };
    const handleClose = () => { cancelClose(); setOpen(false); };

    const handleClickAway = () => {
        if (languageMenuOpenRef.current) return;
        handleClose();
    };

    const handleTriggerEnter = () => { pointerInsideRef.current = true; handleOpen(); };
    const handlePanelEnter = () => { pointerInsideRef.current = true; cancelClose(); };
    const handlePointerLeave = () => { pointerInsideRef.current = false; scheduleClose(); };

    // When the picker closes with the pointer already outside the panel (a
    // language was picked, or the user clicked elsewhere), resume the normal
    // hover-out close; if the pointer is back on the panel, keep it open.
    const handleLanguageMenuOpenChange = useCallback((isOpen) => {
        const wasOpen = languageMenuOpenRef.current;
        languageMenuOpenRef.current = isOpen;
        if (wasOpen && !isOpen && !pointerInsideRef.current) scheduleClose();
    }, [scheduleClose]);

    const handleOpenProfile = () => {
        handleClose();
        handleOpenProfileConfigModal();
    };

    // The card header goes to the public profile page (settings stay in the
    // modal behind the "profile settings" row).
    const handleViewProfile = () => {
        handleClose();
        openProfile(user);
    };

    const handleChangeTheme = (mode) => {
        setAppearance(mode);
    };

    const handleChangeLanguage = (code) => setLanguage(code);

    const handleLogout = () => {
        handleClose();
        window.logout();
    };

    const trigger = avatarOnly ? (
        <button
            type="button"
            ref={anchorRef}
            className={`cs-user-select-avatar ${open ? 'active' : ''}`}
            aria-label={user?.username}
            aria-haspopup="menu"
            aria-expanded={open}
            onMouseEnter={handleTriggerEnter}
            onMouseLeave={handlePointerLeave}
            // A tap fires mouseenter before click, so a toggle would close what the tap
            // opened; click only opens (click-away, hover-out and Escape close).
            onClick={handleOpen}
            onKeyDown={event => { if (event.key === 'Escape') handleClose(); }}
        >
            <UserAvatar user={user} size={28} link={false} />
        </button>
    ) : null;

    return (
        <div className={avatarOnly ? 'cs-user-select-container cs-user-select-container--avatar' : 'cs-user-select-container mt-1'}>
            {trigger || <div
                ref={anchorRef}
                className={`cs-user-select-trigger ${open ? 'active' : ''}`}
                onMouseEnter={handleTriggerEnter}
                onMouseLeave={handlePointerLeave}
                onClick={handleToggle}
            >
                {
                    user?.avatar
                        ? <img className="user-select-avatar-effect" src={user.avatar} alt={user.username} />
                        : null
                }
                <div className="user-info-wrapper">
                    <UserAvatar user={user} size={32} link={false} />
                    <div className="user-text">
                        <div className="user-name-row">
                            {user?.verified ? (
                                <IconRosetteDiscountCheckFilled size={14} className="trigger-verified-icon" />
                            ) : null}
                            <Text.span weight={500} contrast={10}>{user?.username}</Text.span>
                        </div>
                        <Text.span contrast={6}>
                            {user?.nickname ? `@${user?.nickname}` : user?.email?.address}
                        </Text.span>
                    </div>
                </div>
                <IconSelector className={`chevron-icon ${open ? 'rotated' : ''}`} />
            </div>}

            <MuiPopper
                open={open}
                anchorEl={anchorRef.current}
                placement={avatarOnly ? 'bottom-end' : 'top-start'}
                style={{ zIndex: 1300 }}
                modifiers={[{ name: 'offset', options: { offset: [0, avatarOnly ? 8 : 6] } }]}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <div onMouseEnter={handlePanelEnter} onMouseLeave={handlePointerLeave}>
                        <UserDropdown
                            user={user}
                            appearance={appearance}
                            onChangeTheme={handleChangeTheme}
                            language={language}
                            languages={languages}
                            onChangeLanguage={handleChangeLanguage}
                            onLanguageMenuOpenChange={handleLanguageMenuOpenChange}
                            languageMenuPlacement={avatarOnly ? 'left-start' : 'right-start'}
                            items={items.map(item => ({ ...item, onClick: () => { handleClose(); item.onClick?.(); } }))}
                            onOpenProfile={handleOpenProfile}
                            onViewProfile={handleViewProfile}
                            onLogout={handleLogout}
                        />
                    </div>
                </ClickAwayListener>
            </MuiPopper>
        </div>
    );
}
